import {AfterViewInit, Component, ElementRef, inject, Input, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BarSeriesOption, EChartsCoreOption, EChartsType, init, PieSeriesOption} from "echarts";
import type {LineSeriesOption} from 'echarts/charts';
import {SerieConfig} from "../shared-chart-legend/shared-chart-legend.component";
import lodash from "lodash";
import {nonNull} from "../../../../common/utils";

@Component({
    selector: 'app-service-monitoring-chart',
    templateUrl: './service-monitoring-chart.component.html',
    styleUrl: './service-monitoring-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ServiceMonitoringChartComponent implements AfterViewInit {
  private elementRef: ElementRef = inject(ElementRef);
  @ViewChild("chart") chartElement: ElementRef;
  private chart: EChartsType;
  private calculatedHeight: number;

  @Input() type: 'bar' | 'line' | 'pie';
  @Input() chartTitle: string;
  @Input() totalCount: number;
  private series: ChartSerie[];
  private serieConfig: SerieConfig[];

  ngAfterViewInit() {
    this.updateChart();
  }

  @Input("series")
  set setSeries(series: ChartSerie[]) {
    this.series = series;
    this.updateChart();
  }

  @Input("serieConfig")
  set setSerieConfig(serieConfig: SerieConfig[]) {
    this.serieConfig = serieConfig;
    this.updateChart();
  }

  updateChart() {
    if (!this.chartElement) {
      return;
    }
    this.calculateHeightAndUpdateSize();
    if (!this.chart) {
      this.initialise();
    }
    this.chart.showLoading();
    const series = (this.series || []).filter(nonNull);
    const config: EChartsCoreOption = {
      title: {
        text: `${this.chartTitle}${this.totalCount ? ' (' + this.totalCount + ')' : ''}`
      },
      tooltip: this.type === "pie" ? {
        trigger: 'item',
        position: 'top',
        formatter: '{a} <br/>{b} : {c} ({d}%)'
      } : {
        trigger: 'axis',
        position: 'top'
      },
      legend: series?.length && this.serieConfig?.length ? {
        data: series.map(s => s.name),
        selected: lodash.fromPairs(this.serieConfig.map(s => [s.name, s.selected])),
        show: false
      } : undefined,
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          formatter: function (value) {
            const date = new Date(value);
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');

            if (date.getMilliseconds() === 0 && seconds === '00') {
              // Show only HH:mm if seconds are zero
              return `${hours}:${minutes}`;
            } else {
              return `${hours}:${minutes}:${seconds}`;
            }
          }
        },
        show: this.type !== "pie"
      },
      yAxis: {
        type: 'value'
      },
      series: series.map(s => ({
        ...s.serie,
        showSymbol: false,
        smooth: true
      }))
    };
    this.chart.setOption(config, { notMerge: true });
    if (series?.length) {
      this.chart.hideLoading();
    }
  }

  private initialise() {
    this.chart = init(this.chartElement.nativeElement, null, {
      width: this.elementRef.nativeElement.offsetWidth,
      height: this.calculatedHeight
    });
    this.updateSize();
    window.addEventListener('resize', () => this.calculateHeightAndUpdateSize());
  }

  private updateSize() {
    this.chart.resize({
      width: this.elementRef.nativeElement.offsetWidth,
      height: this.calculatedHeight
    });
  }

  private calculateHeightAndUpdateSize() {
    this.calculatedHeight = this.elementRef.nativeElement.offsetHeight - 10;
    if (this.chart && (this.chart.getHeight() !== this.calculatedHeight || this.chart.getWidth() !== this.elementRef.nativeElement.offsetWidth)) {
      this.updateSize();
    }
  }
}

export interface ChartSerie {
  serie: LineSeriesOption | BarSeriesOption | PieSeriesOption;
  name: string;
}
